import React, { useEffect, useMemo, useState, useContext, useRef } from 'react';
import toast from '../utils/toast';
import { 
  Activity, CalendarDays, CreditCard, DollarSign, Pill, Users, 
  User, ChevronDown, LogOut, KeyRound, Eye, EyeOff, X, Loader2 
} from 'lucide-react';
import { api } from '../config/api';
import { AuthContext } from '../context/AuthContext';

const toLocalYmd = (dateValue) => {
  const d = new Date(dateValue);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${day}-${m}-${y}`;
};

const formatCurrency = (value) => {
  const num = Number(value || 0);
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(num);
};

const getPaymentAmount = (item) => (
  Number(item?.amount || item?.fee || item?.paymentAmount || item?.totalAmount || 0)
);

const AdminDashboard = () => {
  const { user, logout } = useContext(AuthContext);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Profile & Password States
  const [profileOpen, setProfileOpen] = useState(false);
  const [showPassModal, setShowPassModal] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [passData, setPassData] = useState({ current: '', new: '', confirm: '' });
  const [showPass, setShowPass] = useState({ current: false, new: false, confirm: false });
  const dropdownRef = useRef(null);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setProfileOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await api.get('/appointments/all');
        if (res.data?.success) {
          setAppointments(res.data.data || []);
        }
      } catch (err) {
        toast.error('FAILED TO LOAD DASHBOARD DATA');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (passData.new !== passData.confirm) return toast.error("New passwords do not match");

    setIsUpdating(true);
    try {
      const res = await api.post('/user-management/change-password', {
        currentPassword: passData.current,
        newPassword: passData.new
      });
      if (res.data.success) {
        toast.success("Password Updated! Logging out...");
        setTimeout(() => logout(), 2000);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update password");
    } finally {
      setIsUpdating(false);
    }
  };

  const todayYmd = useMemo(() => toLocalYmd(new Date()), []);

  const todaysAppointments = useMemo(
    () => appointments.filter((item) => item.appointmentDate === todayYmd),
    [appointments, todayYmd]
  );

  const todaysRevenue = useMemo(() => (
    todaysAppointments
      .filter((item) => String(item.paymentStatus || '').toLowerCase() === 'paid')
      .reduce((sum, item) => sum + getPaymentAmount(item), 0)
  ), [todaysAppointments]);

  const pendingPayments = useMemo(() => (
    appointments.filter((item) => String(item.paymentStatus || '').toLowerCase() === 'unpaid')
  ), [appointments]);

  const pendingAmount = useMemo(() => (
    pendingPayments.reduce((sum, item) => sum + getPaymentAmount(item), 0)
  ), [pendingPayments]);

  const pharmacySales = useMemo(() => (
    appointments
      .filter((item) => String(item.department || '').toLowerCase().includes('pharmacy'))
      .reduce((sum, item) => sum + getPaymentAmount(item), 0)
  ), [appointments]);

  const doctorRevenue = useMemo(() => {
    const map = new Map();
    appointments.forEach((item) => {
      if (String(item.paymentStatus || '').toLowerCase() !== 'paid') return;
      const name = item.doctorName || item.doctorId || 'Unknown';
      const current = map.get(name) || 0;
      map.set(name, current + getPaymentAmount(item));
    });
    return Array.from(map.entries())
      .map(([name, amount]) => ({ name, amount }))
      .sort((a, b) => b.amount - a.amount)
      .slice(0, 8);
  }, [appointments]);

  const maxDoctorRevenue = useMemo(() => (
    doctorRevenue.reduce((max, item) => Math.max(max, item.amount), 0)
  ), [doctorRevenue]);

  return (
    <div className="min-h-screen bg-slate-50 text-slate-800 font-sans">
      <div className="mx-auto max-w-7xl px-6 py-8 flex flex-col gap-8">
        
        {/* HEADER WITH PROFILE */}
        <div className="flex items-center justify-between pb-6 border-b border-slate-200">
          <div className="flex items-center gap-3.5">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#1b2559] text-white shadow-md shadow-[#1b2559]/20">
              <Activity size={24} className="text-[#e11d48]" />
            </div>
            <div>
              <p className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#e11d48]">Asha The Hope</p>
              <h1 className="text-2xl font-black text-[#1b2559]">Admin Dashboard</h1>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <div className="hidden rounded-xl border border-slate-200 bg-white px-4 py-2 text-xs font-bold uppercase tracking-[0.15em] text-slate-600 shadow-sm md:block">
              {todayYmd}
            </div>

            {/* PROFILE DROPDOWN */}
            <div className="relative" ref={dropdownRef}>
              <button 
                onClick={() => setProfileOpen(!profileOpen)}
                className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-1.5 pr-4 shadow-sm hover:border-slate-300 transition"
              >
                <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#1b2559] text-white font-bold">
                  <User size={18} strokeWidth={2.5} className="text-[#e11d48]" />
                </div>
                <div className="hidden text-left md:block">
                  <p className="text-xs font-bold text-[#1b2559] leading-tight uppercase">{user?.fullName || 'User'}</p>
                  <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">{user?.role || 'Staff'}</p>
                </div>
                <ChevronDown size={14} className={`text-slate-400 transition-transform ${profileOpen ? 'rotate-180' : ''}`} />
              </button>

              {profileOpen && (
                <div className="absolute right-0 mt-2 w-56 rounded-2xl border border-slate-200 bg-white p-2 shadow-xl z-50">
                  <button 
                    onClick={() => { setShowPassModal(true); setProfileOpen(false); }}
                    className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-100"
                  >
                    <KeyRound size={16} className="text-[#1b2559]" /> Change Password
                  </button>
                  <button 
                    onClick={() => { logout(); setProfileOpen(false); }}
                    className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold text-[#e11d48] transition hover:bg-red-50"
                  >
                    <LogOut size={16} /> Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* STAT CARDS */}
        <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-4">
          
          {/* Card 1 */}
          <div className="rounded-3xl border border-slate-200/80 bg-white p-6 shadow-sm hover:shadow-md transition">
            <div className="flex items-center justify-between">
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Today’s Appointments</p>
              <div className="p-2 rounded-xl bg-[#1b2559]/5 text-[#1b2559]">
                <CalendarDays size={18} />
              </div>
            </div>
            <div className="mt-4 text-4xl font-extrabold text-[#1b2559]">{todaysAppointments.length}</div>
            <p className="mt-2 text-xs font-medium text-slate-400">Scheduled for today</p>
          </div>

          {/* Card 2 */}
          <div className="rounded-3xl border border-slate-200/80 bg-white p-6 shadow-sm hover:shadow-md transition">
            <div className="flex items-center justify-between">
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Today’s Revenue</p>
              <div className="p-2 rounded-xl bg-emerald-50 text-emerald-600">
                <DollarSign size={18} />
              </div>
            </div>
            <div className="mt-4 text-3xl font-extrabold text-[#1b2559]">{formatCurrency(todaysRevenue)}</div>
            <p className="mt-2 text-xs font-medium text-slate-400">Paid appointments only</p>
          </div>

          {/* Card 3 */}
          <div className="rounded-3xl border border-slate-200/80 bg-white p-6 shadow-sm hover:shadow-md transition">
            <div className="flex items-center justify-between">
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Pharmacy Sales</p>
              <div className="p-2 rounded-xl bg-indigo-50 text-indigo-600">
                <Pill size={18} />
              </div>
            </div>
            <div className="mt-4 text-3xl font-extrabold text-[#1b2559]">{formatCurrency(pharmacySales)}</div>
            <p className="mt-2 text-xs font-medium text-slate-400">Department tagged Pharmacy</p>
          </div>

          {/* Card 4 */}
          <div className="rounded-3xl border border-slate-200/80 bg-white p-6 shadow-sm hover:shadow-md transition">
            <div className="flex items-center justify-between">
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Pending Payments</p>
              <div className="p-2 rounded-xl bg-red-50 text-[#e11d48]">
                <CreditCard size={18} />
              </div>
            </div>
            <div className="mt-4 text-3xl font-extrabold text-[#e11d48]">{pendingPayments.length}</div>
            <p className="mt-2 text-xs font-medium text-slate-400">{formatCurrency(pendingAmount)} outstanding</p>
          </div>

        </div>

        {/* DOCTOR REVENUE SECTION */}
        {/* <div className="rounded-3xl border border-slate-200/80 bg-white p-6 shadow-sm">
          <div className="flex items-center justify-between pb-4 border-b border-slate-100">
            <div>
              <p className="text-xs font-bold uppercase tracking-widest text-[#e11d48]">Doctor-wise Revenue</p>
              <h2 className="mt-1 text-xl font-extrabold text-[#1b2559]">Top Performers</h2>
            </div>
            <div className="flex items-center gap-2 text-xs font-semibold text-slate-500 bg-slate-100 px-3 py-1.5 rounded-full">
              <Users size={14} /> Paid totals
            </div>
          </div>

          {doctorRevenue.length === 0 ? (
            <div className="mt-6 rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-8 text-center text-sm font-medium text-slate-400">
              No paid revenue found yet.
            </div>
          ) : (
            <div className="mt-6 grid gap-6 md:grid-cols-2">
              <div className="space-y-3.5">
                {doctorRevenue.map((doc) => (
                  <div key={doc.name} className="rounded-2xl border border-slate-100 bg-slate-50/50 px-4 py-3">
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-bold text-[#1b2559]">{doc.name}</span>
                      <span className="text-[#e11d48] font-black">{formatCurrency(doc.amount)}</span>
                    </div>
                    <div className="mt-2 h-2.5 w-full rounded-full bg-slate-200/70 overflow-hidden">
                      <div
                        className="h-2.5 rounded-full bg-gradient-to-r from-[#1b2559] to-[#e11d48]"
                        style={{ width: `${maxDoctorRevenue ? (doc.amount / maxDoctorRevenue) * 100 : 0}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex h-full items-end justify-between gap-3 rounded-2xl border border-slate-100 bg-slate-50/50 p-5">
                {doctorRevenue.map((doc) => (
                  <div key={doc.name} className="flex h-full w-full flex-col items-center justify-end gap-2">
                    <div
                      className="w-full rounded-xl bg-gradient-to-t from-[#1b2559] to-[#e11d48] shadow-md shadow-[#1b2559]/10 transition-all duration-300"
                      style={{ height: `${maxDoctorRevenue ? (doc.amount / maxDoctorRevenue) * 100 : 0}%`, minHeight: '16%' }}
                      title={`${doc.name}: ${formatCurrency(doc.amount)}`}
                    />
                    <div className="text-[10px] font-bold text-slate-500 truncate w-full text-center">{doc.name.split(' ')[0]}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div> */}

        {loading && (
          <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-xs font-bold uppercase tracking-widest text-slate-400 animate-pulse text-center">
            Loading dashboard data...
          </div>
        )}
      </div>

      {/* CHANGE PASSWORD MODAL */}
      {showPassModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-4">
          <div className="w-full max-w-md bg-white border border-slate-200 rounded-3xl p-6 shadow-2xl">
            <div className="flex justify-between items-center mb-6 border-b border-slate-100 pb-4">
              <h2 className="text-lg font-extrabold text-[#1b2559] flex items-center gap-2">
                <KeyRound className="text-[#e11d48]" size={20}/> Security & Password
              </h2>
              <button onClick={() => setShowPassModal(false)} className="text-slate-400 hover:text-slate-600"><X size={20} /></button>
            </div>

            <form onSubmit={handlePasswordSubmit} className="space-y-4">
              <PassField 
                label="Current Password" value={passData.current} 
                show={showPass.current} onToggle={() => setShowPass({...showPass, current: !showPass.current})}
                onChange={(e) => setPassData({...passData, current: e.target.value})} 
              />
              <PassField 
                label="New Password" value={passData.new} 
                show={showPass.new} onToggle={() => setShowPass({...showPass, new: !showPass.new})}
                onChange={(e) => setPassData({...passData, new: e.target.value})} 
              />
              <PassField 
                label="Confirm New Password" value={passData.confirm} 
                show={showPass.confirm} onToggle={() => setShowPass({...showPass, confirm: !showPass.confirm})}
                onChange={(e) => setPassData({...passData, confirm: e.target.value})} 
              />

              <button 
                disabled={isUpdating} 
                type="submit" 
                className="w-full bg-[#1b2559] hover:bg-[#12193b] py-3.5 rounded-xl font-bold text-white transition flex items-center justify-center gap-2 shadow-lg shadow-[#1b2559]/20"
              >
                {isUpdating ? <Loader2 className="animate-spin" size={18} /> : 'Update Password'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// Reusable Password Field Component
const PassField = ({ label, show, onToggle, ...props }) => (
  <div className="space-y-1.5">
    <label className="text-[10px] font-bold uppercase tracking-wider text-slate-600">{label}</label>
    <div className="relative">
      <input 
        {...props} type={show ? 'text' : 'password'} required
        className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-900 focus:bg-white focus:border-[#1b2559] focus:ring-1 focus:ring-[#1b2559] outline-none transition normal-case" 
      />
      <button type="button" onClick={onToggle} className="absolute right-3 top-3.5 text-slate-400 hover:text-slate-600">
        {show ? <EyeOff size={16} /> : <Eye size={16} />}
      </button>
    </div>
  </div>
);

export default AdminDashboard;